import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import { Box, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useContactCenterTheme } from "../../../virtual-receptionist/theming/ContactCenterThemeContext";
import { AudioTone } from "../types";

type AudioOptionCardProps = {
  tone: AudioTone;
  selected: boolean;
  previewing: boolean;
  onSelect: () => void;
  onPreview: () => void;
};

export function AudioOptionCard({ tone, selected, previewing, onSelect, onPreview }: AudioOptionCardProps) {
  const { theme } = useContactCenterTheme();
  const isSilent = tone.id === "silent";

  return (
    <Box
      role="radio"
      aria-checked={selected}
      onClick={(event) => {
        event.stopPropagation();
        onSelect();
      }}
      sx={{
        minHeight: 30,
        borderRadius: "6px",
        px: 0.75,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        cursor: "pointer",
        backgroundColor: "transparent",
        transition: "background-color 0.16s ease",
        "&:hover": {
          backgroundColor: alpha(theme.shell.accent, 0.08),
        },
      }}
    >
      <Typography
        sx={{
          fontSize: "0.72rem",
          fontWeight: selected ? 800 : 650,
          color: theme.shell.textPrimary,
          lineHeight: 1.2,
        }}
        noWrap
      >
        {tone.label}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: "0 0 auto" }}>
        {isSilent ? (
          <Box
            aria-label="No sound"
            sx={{
              width: 22,
              height: 22,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              color: theme.shell.textSecondary,
              backgroundColor: alpha(theme.shell.textSecondary, 0.08),
            }}
          >
            <VolumeOffRoundedIcon sx={{ fontSize: 15 }} />
          </Box>
        ) : (
          <IconButton
            size="small"
            aria-label={previewing ? `Stop ${tone.label}` : `Preview ${tone.label}`}
            onClick={(event) => {
              event.stopPropagation();
              onPreview();
            }}
            sx={{
              width: 22,
              height: 22,
              color: previewing ? theme.shell.accent : theme.shell.textSecondary,
              border: `1px solid ${theme.shell.border}`,
              backgroundColor: previewing ? alpha(theme.shell.accent, 0.08) : theme.shell.bg,
              "&:hover": {
                color: theme.shell.accent,
                backgroundColor: alpha(theme.shell.accent, 0.08),
              },
            }}
          >
            {previewing ? <PauseRoundedIcon sx={{ fontSize: 15 }} /> : <PlayArrowRoundedIcon sx={{ fontSize: 15 }} />}
          </IconButton>
        )}

        {selected ? (
          <CheckCircleRoundedIcon sx={{ fontSize: 15, color: theme.shell.textPrimary }} />
        ) : (
          <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 15, color: theme.shell.textSecondary }} />
        )}
      </Box>
    </Box>
  );
}
